import { useEffect, useId, useState, type MouseEvent as ReactMouseEvent } from "react";
import { num } from "../lib/format";
import { TIP, cn } from "./ui";
import type { TrendPoint } from "../lib/types";

type Series = "supply" | "shipment";
const SERIES_LABEL: Record<Series, string> = { supply: "Поставка", shipment: "Отгрузка" };
const SERIES_COLOR: Record<Series, string> = { supply: "rgb(var(--accent))", shipment: "rgb(var(--accent-2))" };

/** Largest single-step rise and largest single-step fall in one series — drawn on the chart as a
 * small ▲/▼ badge (2026-09-12, explicit request: "eng katta sakrashlarni va tushishlarni
 * korsataylik"). ok/danger are the palette's existing semantic colours (already used for
 * status elsewhere), not new brand hues. */
function biggestMove(points: TrendPoint[], get: (p: TrendPoint) => number) {
  let up: { idx: number; delta: number } | null = null;
  let down: { idx: number; delta: number } | null = null;
  for (let i = 1; i < points.length; i++) {
    const d = get(points[i]) - get(points[i - 1]);
    if (d > 0 && (!up || d > up.delta)) up = { idx: i, delta: d };
    if (d < 0 && (!down || d < down.delta)) down = { idx: i, delta: d };
  }
  return { up, down };
}

/** Movement per day: Поставка vs Отгрузка as two smooth lines with a soft area fill. Two distinct hues
 * — blue accent for Поставка, green-teal for Отгрузка (2026-09-12: "ranglar ikki xil bo'lsin, ajralib
 * tursin"). Custom hover tooltip ("hunuk turibdi" about the native one). The biggest jump/drop badge
 * is picked per series — the legend doubles as a selector (Отгрузка by default — 2026-09-12,
 * "otgruzkaniki kerak bolsin va hohlagan line ni tanlasa bolsin").
 * 2026-09-28: drawn at the container's real pixel width (ResizeObserver) instead of a scaled
 * viewBox, so axis text stays 11px on any screen; monotone curves; HTML tooltip. */
export function TrendChart({ points }: { points: TrendPoint[] }) {
  const gid = useId().replace(/:/g, "");
  const [hover, setHover] = useState<number | null>(null);
  const [highlight, setHighlight] = useState<Series>("shipment");
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const W = useWidth(box);
  if (points.length < 2) return <div className="p-6 text-center text-sm text-text-2">Недостаточно данных</div>;

  const H = W < 520 ? 200 : 260;
  const pad = { l: 8, r: 52, t: 16, b: 26 };
  const iw = Math.max(1, W - pad.l - pad.r);
  const ih = H - pad.t - pad.b;
  const max = Math.max(1, ...points.map((p) => Math.max(p.supply, p.shipment))) * 1.1;
  const slot = iw / (points.length - 1 || 1);
  const x = (i: number) => pad.l + i * slot;
  const yTop = (v: number) => pad.t + ih - (v / max) * ih;
  const baseline = pad.t + ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ v: Math.round(max * f), y: pad.t + ih - f * ih }));
  const everyN = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(iw / 64))));
  const { up, down } = biggestMove(points, (p) => p[highlight]);

  const linePath = (get: (p: TrendPoint) => number) => smoothPath(points.map((p, i) => [x(i), yTop(get(p))]));
  const areaPath = (get: (p: TrendPoint) => number) =>
    `${linePath(get)} L${x(points.length - 1).toFixed(1)},${baseline} L${x(0).toFixed(1)},${baseline} Z`;

  const onMove = (e: ReactMouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((e.clientX - rect.left - pad.l) / slot)));
    setHover(idx);
  };

  const hp = hover !== null ? points[hover] : null;
  const hx = hover !== null ? x(hover) : 0;
  const flip = hx > W - 170;
  const fmtDate = (d: string) => `${d.slice(8)}.${d.slice(5, 7)}`;

  return (
    <>
      <div ref={setBox} className="relative w-full" style={{ height: H }}>
        {W > 0 && (
          <svg
            width={W}
            height={H}
            className="block cursor-crosshair"
            role="img"
            aria-label="Движение товара по дням"
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <linearGradient id={`${gid}-supply`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.14" />
                <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
              </linearGradient>
              <linearGradient id={`${gid}-shipment`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--accent-2))" stopOpacity="0.12" />
                <stop offset="100%" stopColor="rgb(var(--accent-2))" stopOpacity="0" />
              </linearGradient>
            </defs>
            {ticks.map((t) => (
              <g key={t.y}>
                <line x1={pad.l} x2={W - pad.r} y1={t.y} y2={t.y} stroke="rgb(var(--border))" strokeWidth={1} />
                <text x={W - pad.r + 8} y={t.y + 4} fontSize="11" fill="rgb(var(--text-2))" className="tnum">
                  {num(t.v)}
                </text>
              </g>
            ))}

            <path d={areaPath((p) => p.shipment)} fill={`url(#${gid}-shipment)`} />
            <path d={linePath((p) => p.shipment)} fill="none" stroke="rgb(var(--accent-2))" strokeWidth={2} strokeLinejoin="round" />
            <path d={areaPath((p) => p.supply)} fill={`url(#${gid}-supply)`} />
            <path d={linePath((p) => p.supply)} fill="none" stroke="rgb(var(--accent))" strokeWidth={2} strokeLinejoin="round" />

            {/* Biggest single-day rise / fall in the selected series — just the number (2026-09-12:
                "faqat raqam chiqsin"), clamped inside the plot area. */}
            {up && (
              <text
                x={x(up.idx)}
                y={Math.max(yTop(points[up.idx][highlight]) - 8, pad.t + 10)}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="rgb(var(--ok))"
                className="tnum"
              >
                +{num(up.delta)}
              </text>
            )}
            {down && (
              <text
                x={x(down.idx)}
                y={Math.min(yTop(points[down.idx][highlight]) + 16, baseline - 4)}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="rgb(var(--danger))"
                className="tnum"
              >
                {num(down.delta)}
              </text>
            )}

            {points.map((p, i) => {
              const last = points.length - 1;
              // regular ticks + the last day, unless it would crowd the previous tick
              const show = i === last || (i % everyN === 0 && x(last) - x(i) >= 52);
              if (!show) return null;
              const anchor = i === 0 ? "start" : i === last ? "end" : "middle";
              return (
                <text key={p.date} x={x(i)} y={H - 8} textAnchor={anchor} fontSize="11" fill="rgb(var(--text-2))" className="tnum">
                  {fmtDate(p.date)}
                </text>
              );
            })}

            {hp && (
              <g pointerEvents="none">
                <line x1={hx} x2={hx} y1={pad.t} y2={baseline} stroke="rgb(var(--border-strong))" strokeDasharray="3 3" />
                <circle cx={hx} cy={yTop(hp.supply)} r={4} fill="rgb(var(--accent))" stroke="rgb(var(--surface))" strokeWidth={2} />
                <circle cx={hx} cy={yTop(hp.shipment)} r={4} fill="rgb(var(--accent-2))" stroke="rgb(var(--surface))" strokeWidth={2} />
              </g>
            )}
          </svg>
        )}
        {hp && (
          <div
            className={cn("pointer-events-none absolute top-2 z-10 min-w-[150px]", TIP)}
            style={flip ? { right: W - hx + 12 } : { left: hx + 12 }}
          >
            <div className="mb-1 font-semibold">
              {fmtDate(hp.date)}.{hp.date.slice(0, 4)}
            </div>
            {(["supply", "shipment"] as Series[]).map((sr) => (
              <div key={sr} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-bg/70">
                  <i className="inline-block h-2 w-2 rounded-sm" style={{ background: SERIES_COLOR[sr] }} />
                  {SERIES_LABEL[sr]}
                </span>
                <span className="font-semibold tnum">{num(hp[sr])}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-2">
        {(["supply", "shipment"] as Series[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setHighlight(s)}
            title="Показать максимальный рост и падение для этой линии"
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors",
              highlight === s ? "border-border bg-surface-3 font-medium text-text" : "border-transparent hover:bg-surface-3",
            )}
          >
            <i className="inline-block h-2 w-2 rounded-sm" style={{ background: SERIES_COLOR[s] }} />
            {SERIES_LABEL[s]}
          </button>
        ))}
        {up && <Legend color="rgb(var(--ok))" label={`Макс. рост +${num(up.delta)}`} />}
        {down && <Legend color="rgb(var(--danger))" label={`Макс. падение ${num(down.delta)}`} />}
      </div>
    </>
  );
}

/** Container width, kept current by a ResizeObserver. */
function useWidth(el: HTMLElement | null): number {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver((entries) => setW(Math.round(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return w;
}

/** Monotone cubic (Fritsch–Carlson) path through the points — smooth, but never overshoots, so a
 * zero day never dips below the baseline. */
function smoothPath(pts: Array<[number, number]>): string {
  const n = pts.length;
  if (n < 3) return pts.map(([px, py], i) => `${i ? "L" : "M"}${px.toFixed(1)},${py.toFixed(1)}`).join(" ");
  const dx: number[] = [];
  const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1][0] - pts[i][0]);
    m.push((pts[i + 1][1] - pts[i][1]) / dx[i]);
  }
  const t: number[] = [m[0]];
  for (let i = 1; i < n - 1; i++) t.push(m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2);
  t.push(m[n - 2]);
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      t[i] = 0;
      t[i + 1] = 0;
      continue;
    }
    const a = t[i] / m[i];
    const b = t[i + 1] / m[i];
    const h = a * a + b * b;
    if (h > 9) {
      const k = 3 / Math.sqrt(h);
      t[i] = k * a * m[i];
      t[i + 1] = k * b * m[i];
    }
  }
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const h = dx[i] / 3;
    d += ` C${(x0 + h).toFixed(1)},${(y0 + t[i] * h).toFixed(1)} ${(x1 - h).toFixed(1)},${(y1 - t[i + 1] * h).toFixed(1)} ${x1.toFixed(1)},${y1.toFixed(1)}`;
  }
  return d;
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 px-1">
      <i className="inline-block h-2 w-2 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

export function CategoryBars({
  rows,
  onPick,
  fmt = num,
  total: grandTotal,
}: {
  rows: Array<{ category: string; units: number }>;
  /** denominator for the %; defaults to the sum of `rows` */
  total?: number;
  onPick?: (category: string) => void;
  fmt?: (n: number) => string;
}) {
  if (!rows.length) return <div className="py-8 text-center text-sm text-text-2">Нет данных</div>;
  const max = Math.max(...rows.map((r) => r.units), 1);
  const total = (grandTotal && grandTotal > 0 ? grandTotal : rows.reduce((a, r) => a + Math.max(0, r.units), 0)) || 1;
  // One accent hue; rank fades from full strength to ~45% so the bars read as a ranking.
  const shade = (i: number) => Math.max(0.45, 1 - i * 0.07);
  return (
    <div className="flex flex-col gap-3.5">
      {rows.map((r, i) => (
        <button key={r.category} type="button" onClick={() => onPick?.(r.category)} className="group text-left">
          <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate font-medium text-text group-hover:text-accent">{r.category}</span>
            <span className="flex-none tnum">
              <span className="font-semibold">{fmt(r.units)}</span>
              <span className="ml-1.5 text-xs text-text-2">{Math.round((Math.max(0, r.units) / total) * 100)}%</span>
            </span>
          </div>
          <span className="block h-2 overflow-hidden rounded-full bg-surface-2">
            <span
              className="block h-full rounded-full bg-accent transition-[width]"
              style={{ width: `${Math.max(2, (r.units / max) * 100)}%`, opacity: shade(i) }}
            />
          </span>
        </button>
      ))}
    </div>
  );
}

/** Tiny inline trend line for a KPI/Stat card — reuses whatever daily series is already on hand
 * (the Обзор movement window), no extra request. Pure SVG, no library. */
export function Sparkline({ values, className }: { values: number[]; className?: string }) {
  const gid = `spark-${useId()}`;
  if (values.length < 2) return null;
  const W = 72;
  const H = 22;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = W / (values.length - 1);
  const pts = values.map((v, i) => [i * step, H - ((v - min) / range) * (H - 2) - 1] as const);
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [firstX] = pts[0];
  const [lastX, lastY] = pts[pts.length - 1];
  const area = `${path} L${lastX.toFixed(1)},${H} L${firstX.toFixed(1)},${H} Z`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className={className} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.16" />
          <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} stroke="none" />
      <path d={path} fill="none" stroke="rgb(var(--accent))" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
      <circle cx={lastX} cy={lastY} r={2} fill="rgb(var(--accent))" />
    </svg>
  );
}

/** One series over time (product stock history) in the same visual language as TrendChart: real
 * pixel width, horizontal grid with right-hand labels, smooth line + soft area, hover tooltip. */
export function StockLineChart({
  points,
  label = "Остаток",
}: {
  points: Array<{ date: string; value: number }>;
  label?: string;
}) {
  const gid = useId().replace(/:/g, "");
  const [hover, setHover] = useState<number | null>(null);
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const W = useWidth(box);
  if (points.length < 2) return <div className="p-6 text-center text-sm text-text-2">Недостаточно точек</div>;

  const H = 220;
  const pad = { l: 8, r: 48, t: 12, b: 26 };
  const iw = Math.max(1, W - pad.l - pad.r);
  const ih = H - pad.t - pad.b;
  const vals = points.map((p) => p.value);
  const max = Math.max(1, ...vals) * 1.1;
  const min = Math.min(0, ...vals);
  const span = max - min || 1;
  const slot = iw / (points.length - 1);
  const x = (i: number) => pad.l + i * slot;
  const y = (v: number) => pad.t + ih - ((v - min) / span) * ih;
  const baseline = y(Math.max(0, min));
  const ticks = [0, 0.5, 1].map((f) => ({ v: Math.round(min + span * f), y: pad.t + ih - f * ih }));
  const everyN = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(iw / 64))));
  const line = smoothPath(points.map((p, i) => [x(i), y(p.value)]));
  const area = `${line} L${x(points.length - 1).toFixed(1)},${baseline} L${x(0).toFixed(1)},${baseline} Z`;
  const fmtDate = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
  const hp = hover !== null ? points[hover] : null;
  const hx = hover !== null ? x(hover) : 0;

  return (
    <div ref={setBox} className="relative w-full" style={{ height: H }}>
      {W > 0 && (
        <svg
          width={W}
          height={H}
          className="block cursor-crosshair"
          role="img"
          aria-label={label}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setHover(Math.max(0, Math.min(points.length - 1, Math.round((e.clientX - r.left - pad.l) / slot))));
          }}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={`${gid}-a`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.14" />
              <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
            </linearGradient>
          </defs>
          {ticks.map((t) => (
            <g key={t.y}>
              <line x1={pad.l} x2={W - pad.r} y1={t.y} y2={t.y} stroke="rgb(var(--border))" />
              <text x={W - pad.r + 8} y={t.y + 4} fontSize="11" fill="rgb(var(--text-2))" className="tnum">
                {num(t.v)}
              </text>
            </g>
          ))}
          <path d={area} fill={`url(#${gid}-a)`} />
          <path d={line} fill="none" stroke="rgb(var(--accent))" strokeWidth={2} strokeLinejoin="round" />
          {points.map((p, i) => {
            const last = points.length - 1;
            if (!(i === last || (i % everyN === 0 && x(last) - x(i) >= 52))) return null;
            return (
              <text key={p.date} x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === last ? "end" : "middle"} fontSize="11" fill="rgb(var(--text-2))" className="tnum">
                {fmtDate(p.date)}
              </text>
            );
          })}
          {hp && (
            <g pointerEvents="none">
              <line x1={hx} x2={hx} y1={pad.t} y2={pad.t + ih} stroke="rgb(var(--border-strong))" strokeDasharray="3 3" />
              <circle cx={hx} cy={y(hp.value)} r={4} fill="rgb(var(--accent))" stroke="rgb(var(--surface))" strokeWidth={2} />
            </g>
          )}
        </svg>
      )}
      {hp && (
        <div
          className={cn("pointer-events-none absolute top-2 z-10", TIP)}
          style={hx > W - 150 ? { right: W - hx + 12 } : { left: hx + 12 }}
        >
          <div className="font-semibold">
            {fmtDate(hp.date)}.{hp.date.slice(0, 4)}
          </div>
          <div className="text-bg/70">
            {label}: <b className="text-bg tnum">{num(hp.value)}</b>
          </div>
        </div>
      )}
    </div>
  );
}
